// Procedural audio via Web Audio API — no external files.
// Every SFX is synthesized on-the-fly. Includes a mute toggle stored in localStorage.

let ctx = null;
function getCtx() {
  if (typeof window === "undefined") return null;
  if (ctx) return ctx;
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  ctx = new AC();
  return ctx;
}

export function isMuted() {
  try { return localStorage.getItem("sfx_muted") === "1"; } catch (e) { return false; }
}
export function setMuted(v) {
  try { localStorage.setItem("sfx_muted", v ? "1" : "0"); } catch (e) { /* noop */ }
}

function envelope(node, ctx, attack, sustain, release, peak = 0.35) {
  const now = ctx.currentTime;
  node.gain.setValueAtTime(0.0001, now);
  node.gain.exponentialRampToValueAtTime(peak, now + attack);
  node.gain.setValueAtTime(peak, now + attack + sustain);
  node.gain.exponentialRampToValueAtTime(0.0001, now + attack + sustain + release);
}

function tone({ freq = 400, type = "sine", attack = 0.005, sustain = 0.03, release = 0.15, peak = 0.25, detune = 0 } = {}) {
  const c = getCtx(); if (!c || isMuted()) return;
  try {
    const osc = c.createOscillator();
    const gain = c.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, c.currentTime);
    if (detune) osc.detune.setValueAtTime(detune, c.currentTime);
    osc.connect(gain).connect(c.destination);
    envelope(gain, c, attack, sustain, release, peak);
    osc.start();
    osc.stop(c.currentTime + attack + sustain + release + 0.05);
  } catch (e) { /* noop */ }
}

function noise({ duration = 0.2, peak = 0.2, filterFreq = 1200 } = {}) {
  const c = getCtx(); if (!c || isMuted()) return;
  try {
    const bufferSize = Math.floor(c.sampleRate * duration);
    const buffer = c.createBuffer(1, bufferSize, c.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / bufferSize);
    const src = c.createBufferSource();
    src.buffer = buffer;
    const filter = c.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.setValueAtTime(filterFreq, c.currentTime);
    const gain = c.createGain();
    src.connect(filter).connect(gain).connect(c.destination);
    envelope(gain, c, 0.005, duration * 0.6, duration * 0.4, peak);
    src.start();
    src.stop(c.currentTime + duration + 0.05);
  } catch (e) { /* noop */ }
}

// SFX presets
export const SFX = {
  step:     () => tone({ freq: 180, type: "triangle", attack: 0.005, sustain: 0.02, release: 0.09, peak: 0.15 }),
  coin:     () => { tone({ freq: 1300, type: "square", attack: 0.002, sustain: 0.02, release: 0.08, peak: 0.2 });
                    setTimeout(() => tone({ freq: 1800, type: "square", attack: 0.002, sustain: 0.02, release: 0.09, peak: 0.18 }), 40); },
  loot:     () => { tone({ freq: 660, type: "sine", attack: 0.005, sustain: 0.03, release: 0.15, peak: 0.22 });
                    setTimeout(() => tone({ freq: 990, type: "sine", attack: 0.005, sustain: 0.03, release: 0.15, peak: 0.22 }), 90); },
  sword:    () => { noise({ duration: 0.15, peak: 0.3, filterFreq: 900 });
                    tone({ freq: 90, type: "sawtooth", attack: 0.001, sustain: 0.015, release: 0.10, peak: 0.25 }); },
  hurt:     () => tone({ freq: 220, type: "sawtooth", attack: 0.001, sustain: 0.02, release: 0.15, peak: 0.25, detune: -20 }),
  magic:    () => { for (let i = 0; i < 3; i++) setTimeout(() => tone({ freq: 800 + i * 200, type: "sine", attack: 0.005, sustain: 0.02, release: 0.15, peak: 0.15 }), i * 60); },
  victory:  () => { [523, 659, 784, 1047].forEach((f, i) => setTimeout(() => tone({ freq: f, type: "triangle", attack: 0.005, sustain: 0.05, release: 0.2, peak: 0.2 }), i * 90)); },
  torch:    () => noise({ duration: 0.4, peak: 0.08, filterFreq: 600 }),
  levelup:  () => { [392, 494, 587, 784].forEach((f, i) => setTimeout(() => tone({ freq: f, type: "sine", attack: 0.005, sustain: 0.05, release: 0.25, peak: 0.22 }), i * 80)); },
  notify:   () => tone({ freq: 900, type: "sine", attack: 0.005, sustain: 0.03, release: 0.12, peak: 0.15 }),
  denied:   () => tone({ freq: 160, type: "square", attack: 0.005, sustain: 0.04, release: 0.15, peak: 0.2 }),
  // Cinematic sting when a Travel vision unlocks: a soft low-noise
  // whoosh, a slow rising fifth (C4 → G4 → C5), and a shimmering high
  // arpeggio tail. Total ~1.4s so it never overlaps the next step.
  visionStinger: () => {
    noise({ duration: 0.6, peak: 0.10, filterFreq: 550 });
    [261.63, 392.00, 523.25].forEach((f, i) => setTimeout(() =>
      tone({ freq: f, type: "sine", attack: 0.02, sustain: 0.18, release: 0.35, peak: 0.16 }),
      i * 140));
    setTimeout(() => {
      [1046.5, 1318.5, 1568.0, 2093.0].forEach((f, i) => setTimeout(() =>
        tone({ freq: f, type: "triangle", attack: 0.005, sustain: 0.04, release: 0.22, peak: 0.09 }),
        i * 70));
    }, 520);
  },
};
